import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { colors } from '@guryou/colors';
import { UserTypes } from '@guryou/core';
import { createUser, stateIsLoading, useEffectOnSuccess } from '@guryou/redux';
import logo from 'assets/img/logo.png';
import { useFeelPanelBranding } from 'components/FeelPanelBrandingContext';
import { clearWidgetAuthIntegrationContext } from 'constants/widgetIntegrationBranding';
import { authInputFocusProps } from './authInputFocusProps';

const SignUp = ({ setView, branding, isWidgetAuthFlow = false, handleCloseModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setPanelBranding } = useFeelPanelBranding();
  const accent = branding?.accentColor ?? 'var(--feel-primary)';
  const logoSrc = branding?.logoUrl ? branding.logoUrl : logo;

  const registerUser = useSelector(state => state.user.createUser);

  // Create Zod schema with translations
  const signupSchema = z
    .object({
      email: z
        .string()
        .min(1, t('SIGNUP_MODAL.validation.emailRequired'))
        .email(t('SIGNUP_MODAL.validation.emailInvalid')),
      password: z
        .string()
        .min(1, t('SIGNUP_MODAL.validation.passwordRequired'))
        .min(8, t('SIGNUP_MODAL.validation.passwordMinLength')),
      confirmPassword: z.string().min(1, t('SIGNUP_MODAL.validation.confirmPasswordRequired')),
      terms: z.boolean().refine(val => val === true, t('SIGNUP_MODAL.validation.termsRequired')),
      emailMarketing: z.boolean().optional(),
      marketing: z.boolean().optional(),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: t('SIGNUP_MODAL.validation.passwordsDontMatch'),
      path: ['confirmPassword'],
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur',
  });

  useEffectOnSuccess([state => state.user.createUser], () => {
    if (handleCloseModal) {
      handleCloseModal();
    }
  });

  const onSubmit = data => {
    if (!isWidgetAuthFlow) {
      clearWidgetAuthIntegrationContext();
      setPanelBranding(null);
    }
    const { email, password, emailMarketing, marketing } = data;

    const agreements = [
      { type: 'email', decision: emailMarketing },
      { type: 'marketing', decision: marketing },
      { type: 'sharing', decision: true },
    ];

    const request = {
      email: email,
      password: password,
      agreements: agreements,
      role: UserTypes.customer,
      clientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
    };

    dispatch(createUser(request));
  };

  if (stateIsLoading(registerUser)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.mandy }}></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header with Logo */}
      <div className="text-center border-b border-gray-100 pb-6">
        <div className="flex justify-center mb-4">
          <img src={logoSrc} alt={branding?.brandName || 'Logo'} className="h-12 w-auto max-w-[200px] object-contain" />
        </div>
        {branding?.brandName ? <p className="text-sm font-medium text-gray-800 mb-1">{branding.brandName}</p> : null}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('SIGNUP_MODAL.createAccount')}</h2>
        <p className="text-sm text-gray-600">{t('SIGNUP_MODAL.joinGuryou')}</p>
      </div>

      {/* Form Content */}
      <div className="space-y-6 p-2 mt-2">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('GENERAL.email')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
              <input
                type="email"
                id="email"
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg text-sm placeholder-gray-500 focus:outline-none transition-colors ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                {...authInputFocusProps(accent, !!errors.email, register('email'))}
                placeholder={t('SIGNUP_MODAL.enterYourEmail')}
              />
            </div>
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('GENERAL.password')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                type="password"
                id="password"
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg text-sm placeholder-gray-500 focus:outline-none transition-colors ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                {...authInputFocusProps(accent, !!errors.password, register('password'))}
                placeholder={t('SIGNUP_MODAL.enterYourPassword')}
              />
            </div>
            {errors.password && (
              <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              {t('ACCOUNT.confirmPassword')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                type="password"
                id="confirmPassword"
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg text-sm placeholder-gray-500 focus:outline-none transition-colors ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                {...authInputFocusProps(accent, !!errors.confirmPassword, register('confirmPassword'))}
                placeholder={t('SIGNUP_MODAL.confirmYourPassword')}
              />
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div>
            <label className="flex items-start">
              <input
                {...register('terms')}
                type="checkbox"
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                style={{ accentColor: accent }}
              />
              <span className="ml-3 text-sm text-gray-700">
                {t('SIGNUP_MODAL.termsAndConditions')}{' '}
                <a
                  href="https://www.iubenda.com/privacy-policy/39480488"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:no-underline"
                  style={{ color: accent }}
                >
                  {t('SIGNUP_MODAL.privacyPolicy')}
                </a>{' '}
                {t('SIGNUP_MODAL.and')}{' '}
                <a
                  href="https://www.iubenda.com/terms-and-conditions/8084171"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:no-underline"
                  style={{ color: accent }}
                >
                  {t('SIGNUP_MODAL.termsOfService')}
                </a>
                .
              </span>
            </label>
            {errors.terms && (
              <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.terms.message}
              </p>
            )}
          </div>

          {/* Agreement Text */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 text-justify">{t('SIGNUP_MODAL.agreementText')}</p>
          </div>

          {/* Marketing Options */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                {...register('emailMarketing')}
                type="checkbox"
                id="emailMarketing"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                style={{ accentColor: accent }}
              />
              <label htmlFor="emailMarketing" className="ml-3 text-sm text-gray-700">
                {t('SIGNUP_MODAL.emailMarketing')}
              </label>
            </div>
            <div className="flex items-center">
              <input
                {...register('marketing')}
                type="checkbox"
                id="marketing"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                style={{ accentColor: accent }}
              />
              <label htmlFor="marketing" className="ml-3 text-sm text-gray-700">
                {t('SIGNUP_MODAL.marketingCommunications')}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('SIGNUP_MODAL.creatingAccount')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
                {t('GENERAL.register')}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Footer with Login Link */}
      <div className="py-6 bg-gray-50 border-t border-gray-100">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-3">{t('SIGNUP_MODAL.alreadyHaveAccount')}</p>
          <button
            type="button"
            onClick={() => setView('login')}
            className="inline-flex items-center text-sm font-semibold transition-colors hover:underline"
            style={{ color: accent }}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            {t('GENERAL.login')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
